import { useSetState } from 'ahooks'
import { useDeepCompareEffect } from 'ahooks'
import {
  type DatePickerViewProps,
  type CascadePickerViewProps,
  type PickerViewProps,
  type CalendarPickerProps,
  PickerView,
  DatePickerView,
  CascadePickerView,
  CalendarPickerView
} from 'antd-mobile'
import classNames from 'classnames'
import React, { useEffect, useMemo, useRef } from 'react'

import { CALENDARPICKER_MAX_DATE, CALENDARPICKER_MIN_DATE } from '@/constants'
import { dayjs } from '@/utils'

import HPopup from '../../HYBVant/HPopup'
import './index.less'

type HPickerViewProps =
  | (DatePickerViewProps & { type: 'datePicker' })
  | (CascadePickerViewProps & { type: 'cascadePicker' })
  | (PickerViewProps & { type: 'picker' })
  | (CalendarPickerProps & { type: 'calendarPicker' })

type BasePickerProps = {
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: (v: any) => void
  onSelect?: (v: any) => void
  title?: string
  visible?: boolean
  className?: string
}

type HDatePickerProps = BasePickerProps & DatePickerViewProps
type HCascadePickerProps = BasePickerProps & CascadePickerViewProps
type HCalendarPickerProps = BasePickerProps & {
  selectionMode?: 'single' | 'range'
} & CalendarPickerProps
type HPickerProps = HPickerViewProps & BasePickerProps

/**
 * 选择器组件
 * @param props
 * @returns
 */
const HPicker: React.FC<HPickerProps> = (props: HPickerProps) => {
  const {
    type = 'picker',
    onClose,
    onCancel,
    onConfirm,
    onSelect,
    title,
    visible,
    value,
    className,
    ...rest
  } = props
  const [{ minDate, tempValue }, setState] = useSetState({
    minDate: null,
    tempValue: value
  })
  const pickerViewRef = useRef<any>(null)

  const PickerViewComponent = useMemo(() => {
    return {
      picker: PickerView,
      datePicker: DatePickerView,
      cascadePicker: CascadePickerView,
      calendarPicker: CalendarPickerView
    }[type] as React.ComponentType<any>
  }, [type])

  const handleConfrim = () => {
    onConfirm?.(tempValue)
  }

  const handleSelect = (v: any) => {
    setState({ tempValue: v })
    onSelect?.(v)
  }

  useDeepCompareEffect(() => {
    visible && setState({ tempValue: value })
    // 修复CalendarPicker 默认滚动到选中的位置
    if (type === 'calendarPicker' && visible) {
      const currentDate = dayjs((value as [Date, Date])?.[0] || new Date())
      queueMicrotask(() => {
        pickerViewRef.current?.jumpTo({
          year: currentDate.year(),
          month: currentDate.month() + 1,
          date: currentDate.date()
        })
      })
    }
  }, [value, visible])

  return (
    <HPopup
      className={classNames('h-picker-popup-wrapper', `h-picker-${type}-popup-wrapper`, className)}
      visible={visible}
      title={title}
      onOk={handleConfrim}
      onClose={onClose}
      onCancel={onCancel || onClose}
    >
      <PickerViewComponent
        ref={pickerViewRef}
        className={`h-picker-view h-picker-view-${type}`}
        value={tempValue}
        onChange={handleSelect}
        {...rest}
      />
    </HPopup>
  )
}

/**
 *  DatePicker 日期选择器
 */
export const HDatePicker = (props: HDatePickerProps) => {
  return <HPicker {...props} type='datePicker' />
}

/**
 * CascadePicker 级联选择器
 */
export const HCascadePicker = (props: HCascadePickerProps) => {
  return <HPicker {...props} type='cascadePicker' />
}

/**
 * CalendarPicker 日历选择器
 */
export const HCalendarPicker = (props: HCalendarPickerProps) => {
  return (
    <HPicker
      selectionMode='single'
      min={CALENDARPICKER_MIN_DATE}
      max={CALENDARPICKER_MAX_DATE}
      {...props}
      type='calendarPicker'
    />
  )
}

export default HPicker
